import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../api.js";
import Layout from "../../components/Layout.js";

const ModelMasterGrid = () => {
  const navigate = useNavigate();
  const [gridData, setGridData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchColor, setSearchColor] = useState("");
  const [searchType, setSearchType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    fetchGridData();
  }, []);

  const fetchGridData = async () => {
    try {
      const response = await apiFetch(
        "/api/modelmaster/grid"
      );
      const data = await response.json();
      if (data.message === "No Record Found") {
        setGridData([]);
        setFilteredData([]);
      } else {
        setGridData(data);
        setFilteredData(data);
      }
    } catch (err) {
      console.error("Error fetching Model Master grid:", err);
      alert("Unable to fetch Model Master data from backend.");
    }
  };

  const handleSearch = async () => {
    try {
      const response = await apiFetch(
        `/api/modelmaster/search?sp_207=${encodeURIComponent(searchColor)}&sp_208=${encodeURIComponent(searchType)}`
      );
      const data = await response.json();
      if (data.message === "No Record Found" || data.length === 0) {
        setFilteredData([]);
        alert("No records found.");
      } else {
        setFilteredData(data);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error("Error searching Model Master:", err);
      alert("Unable to perform search.");
    }
  };

  const handleDelete = async (modelId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this model?"
    );
    if (!confirmDelete) return;
    try {
      const response = await apiFetch(
        `/api/modelmaster/${modelId}`,
        { method: "DELETE" },
      );
      const data = await response.json();
      alert(data.message);
      fetchGridData();
    } catch (error) {
      console.error("Delete Error:", error);
      alert("Error deleting model");
    }
  };

  const handleEdit = async (id) => {
    try {
      console.log("🟢 Editing record ID:", id);
      const response = await apiFetch(
        `/api/modelmaster/edit/${id}`
      );
      if (!response.ok)
        throw new Error(`Failed to fetch record. Status: ${response.status}`);
      const data = await response.json();
      console.log("📦 Received data from backend:", data);
      const { model_header, variant_details } = data;
      if (!model_header || model_header.length === 0) {
        alert("No data found for the selected model.");
        return;
      }
      const variantRows = (variant_details || []).map((row) => ({
        varunq: row.varunq,
        variant: row.variant,
        fuel: row.fuel,
        code: row.code,
        cess: row.cess,
        gst: row.gst,
        rtorate: row.rtorate,
        rtotaxsur: row.rtotaxsur,
        greentax: row.greentax,
        odrate: row.odrate,
        thirdparty: row.thirdparty,
        zd: row.zd,
        pb: row.pb,
        kp: row.kp,
        ep: row.ep,
        fasttag: row.fasttag,
        handlingcharges: row.handlingcharges,
        RTO_OtherCity: row.RTO_OtherCity,
        numberplatecharge: row.numberplatecharge,
        registrationfee: row.registrationfee,
        paiddriver: row.paiddriver,
        pacover: row.pacover,
        smartcard: row.smartcard,
        other: row.other,
        duplicate: row.duplicate,
        hpn: row.hpn,
        horsepower: row.horsepower,
        noofcylinders: row.noofcylinders,
        seatingcapacity: row.seatingcapacity,
        unladenweight: row.unladenweight,
        grossvehicleweight: row.grossvehicleweight,
        typeofbody: row.typeofbody,
        effdatef: row.effdatef,
        effdatet: row.effdatet,
        exshowroom: row.exshowroom,
        activestatus: row.activestatus,
      }));
      const model = model_header[0];
      const editData = {
        sp_202: model.unq,
        model: model.model,
        makename: model.makename,
        make: model.makename,
        effectivefrom: model.effectivefrom,
        effectiveto: model.effectiveto,
        active: model.active === true || model.active === "Y",
        variantRows,
      };
      console.log("✅ Structured edit data:", editData);
      navigate("/Modelmaster", { state: { editData } });
    } catch (err) {
      console.error("❌ Failed to fetch record:", err);
      alert("Unable to fetch record from backend.");
    }
  };

  /*-----------------STYLE---------- */
  const styles = {
    pageContainer: {
      width: "100%",
      minHeight: "100vh",
      backgroundColor: "#f4f6f8",
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      padding: "10px",
      boxSizing: "border-box",
    },
    container: {
      width: "100%",
      maxWidth: "1200px",
      padding: "15px",
      borderRadius: "8px",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      backgroundColor: "#fff",
      boxSizing: "border-box",
    },
    header: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "15px",
      gap: "10px",
    },
    leftHeader: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "8px",
      flex: 1,
      minWidth: "250px",
    },
    title: { fontSize: "1.2rem", fontWeight: "bold", flexShrink: 0 },
    addButton: {
      padding: "8px 16px",
      backgroundColor: "#3a28a0ff",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      whiteSpace: "nowrap",
    },
    searchInput: {
      flex: "1",
      minWidth: "140px",
      padding: "8px",
      borderRadius: "4px",
      border: "1px solid #ccc",
    },
    searchButton: {
      padding: "8px 12px",
      backgroundColor: "#2196F3",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      whiteSpace: "nowrap",
    },
    select: {
      padding: "8px 10px",
      borderRadius: "4px",
      border: "1px solid #ccc",
      backgroundColor: "#fff",
      whiteSpace: "nowrap",
    },
    tableWrapper: { overflowX: "auto" },
    table: { width: "100%", borderCollapse: "collapse", minWidth: "400px" },
    th: {
      padding: "10px",
      textAlign: "left",
      backgroundColor: "#8B1E3F",
      color: "#fff",
      border: "1px solid #ddd",
      whiteSpace: "nowrap",
      fontSize: "0.9rem",
    },
    td: {
      padding: "8px",
      border: "1px solid #ddd",
      wordWrap: "break-word",
      fontSize: "0.85rem",
    },
    actionButton: (bgColor) => ({
      padding: "6px 10px",
      marginRight: "5px",
      backgroundColor: bgColor,
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      minWidth: "60px",
      fontSize: "0.8rem",
    }),
    pagination: {
      marginTop: "12px",
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      fontSize: "0.9rem",
    },
    pageBtn: {
      padding: "6px 12px",
      borderRadius: "4px",
      border: "1px solid #ccc",
      cursor: "pointer",
      background: "#f9f9f9",
      fontSize: "0.85rem",
    },
  };

  return (
    <Layout>
      <div style={styles.pageContainer}>
        <div style={styles.container}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.leftHeader}>
              <div style={styles.title}>Model Master Grid</div>

              <input
                type="text"
                placeholder="Search..."
                value={searchColor}
                onChange={(e) => setSearchColor(e.target.value)}
                style={styles.searchInput}
              />

              <button style={styles.searchButton} onClick={handleSearch}>
                Search
              </button>

              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={styles.select}
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
            </div>
            <button
              style={styles.addButton}
              onClick={() => navigate("/Modelmaster")}
            >
              Add New
            </button>
          </div>

          {/* Table */}
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>MODEL</th>
                  <th style={styles.th}>Make</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row) => (
                    <tr key={row.sp_202}>
                      <td style={styles.td}>{row.Model}</td>
                      <td style={styles.td}>{row.Make}</td>
                      <td style={styles.td}>
                        <button
                          style={styles.actionButton("#4CAF50")}
                          onClick={() => handleEdit(row.sp_202)}
                        >
                          Edit
                        </button>

                        <button
                          style={styles.actionButton("#f44336")}
                          onClick={() => handleDelete(row.sp_202)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td style={styles.td} colSpan={3}>
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={styles.pagination}>
            <button
              style={styles.pageBtn}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </button>
            <span>
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              style={styles.pageBtn}
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ModelMasterGrid;
